import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";

const monacoEPrix: ChauffeurPage = {
  slug: "monaco-e-prix",
  title: "Chauffeur privé Monaco E-Prix (Formule E) – VTC VIP",
  subtitle:
    "Chauffeur privé pendant le Monaco E-Prix : accès au circuit urbain, accueil aéroport de Nice et mises à disposition pendant la Formule E.",
  keywords: [
    "chauffeur monaco e-prix",
    "vtc formule e monaco",
    "chauffeur eprix monaco",
  ],
  hero: {
    title: "Chauffeur privé pendant le Monaco E-Prix",
    subtitle:
      "Formule E sur le circuit urbain de Monaco : un chauffeur dédié quand les rues se ferment et que le paddock devient inaccessible en voiture.",
    keywords: [
      "chauffeur Monaco E-Prix",
      "VTC Formule E",
      "chauffeur circuit urbain Monaco",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé pendant le Monaco E-Prix",
    badges: [
      "Accueil aéroport VIP",
      "Accès circuit urbain",
      "Mise à disposition",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur pendant le Monaco E-Prix",
    items: [
      {
        title: "Circuit urbain et rues fermées",
        description:
          "En mai, la Formule E ferme une partie des rues de Monaco autour du circuit. Le chauffeur connaît les accès ouverts et les déposes au plus près des entrées.",
      },
      {
        title: "Accueil aéroport de Nice",
        description:
          "L'aéroport de Nice Côte d'Azur est à une trentaine de minutes. Accueil pancarte, suivi de vol et transfert direct vers votre hôtel.",
      },
      {
        title: "Paddock et hospitalité",
        description:
          "Prises en charge calées sur les essais, la course et les accès e-Village / paddock, sans stationnement à gérer.",
      },
      {
        title: "Retours de nuit assurés",
        description:
          "Dîners du Carré d'Or et soirées after-race : un chauffeur patiente et garantit le retour tardif.",
      },
    ],
  },
  services: {
    heading: "Services chauffeur pendant le Monaco E-Prix",
    services: [
      {
        title: "Accueil aéroport de Nice",
        text: "Transfert Nice ↔ Monaco avec suivi de vol pendant le week-end de Formule E.",
      },
      {
        title: "Accès au circuit urbain",
        text: "Dépose au plus près des entrées selon les fermetures de rues autour du circuit.",
      },
      {
        title: "Mise à disposition journée",
        text: "Chauffeur dédié pour enchaîner essais, course, paddock et rendez-vous.",
      },
      {
        title: "Soirées & after-race",
        text: "Restaurants et clubs du Carré d'Or, retour de nuit garanti.",
      },
      {
        title: "Navettes partenaires & équipes",
        text: "Coordination multi-véhicules pour sponsors, invités et délégations.",
      },
    ],
  },
  locations: {
    heading: "Points clés pendant le Monaco E-Prix",
    items: [
      {
        name: "Circuit urbain de Monaco",
        description:
          "Tracé autour de Port Hercule, du Casino et du bord de mer.",
      },
      {
        name: "Port Hercule & e-Village",
        description: "Paddock, hospitalités et zones partenaires.",
      },
      {
        name: "Carré d'Or & Casino",
        description: "Hôtel de Paris, Hermitage et Place du Casino.",
      },
      {
        name: "Aéroport de Nice Côte d'Azur",
        description: "Terminaux 1 et 2, à une trentaine de minutes.",
      },
      {
        name: "Beausoleil & hauteurs",
        description: "Résidences et accès depuis les abords de Monaco.",
      },
    ],
  },
  generic: {
    heading: "Le Monaco E-Prix vu d'un chauffeur",
    subheading:
      "En mai, la Formule E investit le circuit urbain de Monaco et referme une partie des rues du centre.",
    paragraphs: [
      "Chaque mois de mai, le Monaco E-Prix installe la Formule E sur le circuit urbain de Monaco, en partie commun avec le tracé du Grand Prix. Des rues se ferment autour de Port Hercule et du Casino : un chauffeur qui connaît les accès encore ouverts vous dépose au plus près de votre entrée.",
      "Les arrivées se concentrent à l'aéroport de Nice Côte d'Azur, à une trentaine de minutes. Suivi de vol et accueil pancarte : nous absorbons les décalages pour vous conduire directement à votre hôtel monégasque.",
      "Le week-end enchaîne essais, course électrique et hospitalités du e-Village. La mise à disposition garde le même chauffeur toute la journée et sécurise les retours de nuit après les soirées.",
    ],
    bullets: [
      "Dépose au plus près du circuit",
      "Suivi de vol en mai",
      "Retours de nuit after-race",
    ],
  },
  process: {
    heading: "Réserver un chauffeur pour le Monaco E-Prix",
    steps: [
      "Indiquez vos dates, votre hôtel et vos accès (tribunes, paddock, e-Village).",
      "Nous calons les itinéraires selon les fermetures de rues autour du circuit.",
      "Le chauffeur se présente en tenue, avec suivi de vol pour vos arrivées à Nice.",
      "Transfert direct ou mise à disposition pour couvrir essais, course et soirées.",
    ],
  },
  faq: [
    {
      q: "Comment réserver un chauffeur privé pour le Monaco E-Prix ?",
      a: "Réservez tôt : en mai, Monaco est très demandé pendant la Formule E. Précisez vos dates, votre hôtel et vos accès pour que nous bloquions un chauffeur dédié sur tout le week-end.",
    },
    {
      q: "Pouvez-vous m'approcher du circuit malgré les rues fermées ?",
      a: "Oui. Le circuit urbain ferme une partie des rues du centre. Le chauffeur emprunte les accès encore ouverts et vous dépose au point autorisé le plus proche de votre entrée.",
    },
    {
      q: "Gérez-vous l'arrivée depuis l'aéroport de Nice ?",
      a: "Oui, avec suivi de vol et accueil pancarte. L'aéroport de Nice Côte d'Azur est à une trentaine de minutes ; nous adaptons la prise en charge en cas de retard.",
    },
    {
      q: "Proposez-vous une mise à disposition sur la journée ?",
      a: "Oui. Un chauffeur dédié reste à votre disposition pour enchaîner essais, course et paddock sans gérer le stationnement monégasque.",
    },
    {
      q: "Assurez-vous les retours après les soirées after-race ?",
      a: "Oui. Les soirées du Carré d'Or se terminent tard : le chauffeur patiente et garantit votre retour de nuit.",
    },
    {
      q: "Pourquoi choisir KS Rent Services pour le Monaco E-Prix ?",
      a: "Notre service de chauffeur privé sur la Côte d'Azur est noté 5,0/5 sur 38 avis Google : chauffeur en tenue, ponctuel et habitué aux contraintes d'accès de Monaco.",
    },
  ],
  relatedLinks: [
    { label: "Voiture avec chauffeur à Monaco", href: "/chauffeur/monaco" },
    { label: "Chauffeur privé Nice", href: "/chauffeur/nice" },
    { label: "Navette aéroport de Nice", href: "/chauffeur/aeroport" },
    {
      label: "Tarifs de location à Monaco",
      href: "/location/location-voiture-monaco",
    },
  ],
  cta: {
    title: "Votre chauffeur pour le Monaco E-Prix",
    text: "Accès circuit, accueil aéroport et soirées : réservez un chauffeur dédié pour le week-end de Formule E.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver un chauffeur",
  },
  seo: {
    title: "Chauffeur Monaco E-Prix (Formule E) — VTC VIP",
    description:
      "Chauffeur privé pendant le Monaco E-Prix (Formule E) : accès au circuit urbain, accueil aéroport de Nice, mise à disposition et retours de nuit. Réservez.",
    canonical: "/chauffeur/monaco-e-prix",
  },
};

export default monacoEPrix;
